import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { BOOKABLE_CONSULTANTS } from "../data/consultants";
import { NoMedicalInfoNote } from "./FormPrivacyNote";

const DEFAULT_TOPICS = [
  "General question",
  "Discuss before booking",
  "Reschedule / cancel an appointment",
  "Diagnostic test query",
  "Other",
];

export default function EnquiryForm({ defaultTopic = "" }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    topic: defaultTopic,
    note: "",
    preferred_consultant: "",
    payment_type: "self_pay",
    preferred_times: "",
    consent: false,
  });

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please tick the consent box so we can reply to you.");
      return;
    }
    setSubmitting(true);
    try {
      const { topic, note, ...rest } = form;
      await api.post("/enquiries", { ...rest, reason: `[${topic}]${note.trim() ? ` ${note.trim()}` : ""}` });
      setSubmitted(true);
      toast.success("We will be back with you shortly.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="enquiry-success" className="p-8 bg-cream-100 border border-bronze-light/40 rounded-md text-center">
        <div className="eyebrow">Thank you</div>
        <h3 className="font-serif text-3xl mt-3">Got it — we’ll be in touch.</h3>
        <p className="mt-4 text-ink-muted">A member of our team will be back with you shortly.</p>
      </div>
    );
  }

  const baseInput =
    "w-full rounded-sm border border-stone-300 bg-white px-4 py-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-bronze-dark/40 focus:border-bronze-dark transition";
  const label = "block text-xs uppercase tracking-[0.18em] text-ink-muted font-medium mb-2";

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Toaster richColors position="top-right" />
      <NoMedicalInfoNote />
      <div className="md:col-span-2">
        <label htmlFor="enquiry-topic" className={label}>What can we help with? *</label>
        <select
          id="enquiry-topic"
          required
          data-testid="enquiry-topic-select"
          className={baseInput}
          value={form.topic}
          onChange={update("topic")}
        >
          <option value="">Please choose…</option>
          {[...DEFAULT_TOPICS, ...(defaultTopic && !DEFAULT_TOPICS.includes(defaultTopic) ? [defaultTopic] : [])].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="enquiry-name" className={label}>Full name *</label>
        <input id="enquiry-name" required autoComplete="name" data-testid="enquiry-full-name-input" className={baseInput} value={form.full_name} onChange={update("full_name")} />
      </div>
      <div>
        <label htmlFor="enquiry-email" className={label}>Email *</label>
        <input id="enquiry-email" required type="email" autoComplete="email" data-testid="enquiry-email-input" className={baseInput} value={form.email} onChange={update("email")} />
      </div>
      <div>
        <label htmlFor="enquiry-phone" className={label}>Phone *</label>
        <input id="enquiry-phone" required type="tel" autoComplete="tel" data-testid="enquiry-phone-input" className={baseInput} value={form.phone} onChange={update("phone")} />
      </div>
      <div>
        <label htmlFor="enquiry-consultant" className={label}>Preferred consultant</label>
        <select id="enquiry-consultant" data-testid="enquiry-consultant-select" className={baseInput} value={form.preferred_consultant} onChange={update("preferred_consultant")}>
          <option value="">No preference</option>
          {BOOKABLE_CONSULTANTS.map((c) => (
            <option key={c.slug} value={c.slug}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2">
        <label htmlFor="enquiry-note" className={label}>Short note (optional, no medical details)</label>
        <input
          id="enquiry-note"
          maxLength={200}
          data-testid="enquiry-reason-input"
          className={baseInput}
          value={form.note}
          onChange={update("note")}
          placeholder="e.g. I'd like to book for my father, or a question about fees"
        />
      </div>
      <div className="md:col-span-2">
        <label htmlFor="enquiry-times" className={label}>Preferred times (if you’d like us to call)</label>
        <input id="enquiry-times" data-testid="enquiry-times-input" className={baseInput} placeholder="e.g. Weekday mornings, or Fridays after 2pm" value={form.preferred_times} onChange={update("preferred_times")} />
      </div>
      <div className="md:col-span-2 flex items-start gap-3 p-5 bg-cream-100 border border-bronze-light/40 rounded-sm">
        <input id="consent" type="checkbox" data-testid="enquiry-consent-checkbox" checked={form.consent} onChange={update("consent")} className="mt-1 accent-bronze-dark" />
        <label htmlFor="consent" className="text-sm text-ink-muted">
          I’m happy for Cambridge Cardiovascular Prevention Clinic to contact me about this enquiry, and to use my information as described in their <Link to="/privacy" className="underline text-bronze-dark">Privacy Notice</Link>.
        </label>
      </div>
      {error && <div data-testid="enquiry-error" className="md:col-span-2 text-sm text-destructive">{error}</div>}
      <div className="md:col-span-2 flex flex-wrap gap-4">
        <button disabled={submitting} data-testid="enquiry-submit-button" type="submit" className="btn-primary disabled:opacity-60">
          {submitting ? "Sending…" : "Send enquiry"}
        </button>
        
      </div>
    </form>
  );
}
